import { define } from './define';

// Keys start with "prizes.". Spanish first; English must have the same keys (TypeScript checks it).
// Prizes for commenting: the settings, a post's prize, and the two public pages people outside the team see.
// Messages with {{double braces}} are message templates for the networks: they are read with t() and no variables.
export const prizes = define({
  es: {
    'prizes.kind.file': 'Archivo',
    'prizes.kind.link': 'Enlace',

    'prizes.settings.title': 'Premios por comentar',
    'prizes.settings.on': 'Activados',
    'prizes.settings.off': 'Desactivados',
    'prizes.settings.enable': 'Usar premios en esta marca',
    'prizes.settings.enableHint': 'Al activarlo, el próximo inicio de sesión en Meta pide permiso para enviar mensajes. Las cuentas conectadas antes hay que volver a conectarlas (Cuentas → Renovar) para darlo; Meta puede pedir además revisar la app.',
    'prizes.settings.retention': 'Guardar a las personas (días)',
    'prizes.settings.retentionHint': 'Pasado este tiempo se borra quién comentó. Al menos 8: Meta da 7 días para responder a un comentario.',
    'prizes.settings.notice': 'Nota en cada mensaje',
    'prizes.settings.noticeHint': 'Dice que el mensaje es automático. No se puede quitar.',

    'prizes.library.title': 'Premios',
    'prizes.library.count': { one: '{count} premio', other: '{count} premios' },
    'prizes.library.empty': 'Todavía no hay premios',
    'prizes.library.emptyHint': 'Añade un archivo para regalar o un enlace a otro sitio.',
    'prizes.library.name': 'Nombre',
    'prizes.library.what': 'Qué es',
    'prizes.library.state': 'Estado',
    'prizes.library.running': { one: 'En marcha en {count} publicación', other: 'En marcha en {count} publicaciones' },
    'prizes.library.archive': 'Archivar',
    'prizes.library.archiveTitle': '¿Archivar «{name}»?',
    'prizes.library.archiveConfirm': 'Las publicaciones que ya lo llevan siguen funcionando; no se podrá elegir para otras.',
    'prizes.state.ready': 'Listo',
    'prizes.state.missing': 'Falta el archivo',
    'prizes.state.archived': 'Archivado',

    'prizes.add.title': 'Añadir un premio',
    'prizes.add.kind': 'Tipo de premio',
    'prizes.add.link': 'Un enlace',
    'prizes.add.file': 'Un archivo guardado aquí',
    'prizes.add.name': 'Nombre',
    'prizes.add.nameHint': 'Lo que la gente ve en la página y en el mensaje.',
    'prizes.add.url': 'Enlace',
    'prizes.add.urlHint': 'Adónde se manda a la persona. Cualquiera que reciba el mensaje puede abrirlo: no enlaces nada privado.',
    'prizes.add.fileLabel': 'Archivo',
    'prizes.add.fileHint': 'Hasta 200 MB. La gente recibe un enlace de descarga que vale poco tiempo y pocas descargas.',
    'prizes.add.fileAria': 'Archivo del premio',
    'prizes.add.chooseFile': 'Elige el archivo que vas a regalar',
    'prizes.add.hashing': 'Comprobando el archivo…',
    'prizes.add.uploading': 'Subiendo… {pct} %',
    'prizes.add.finishing': 'Terminando…',
    'prizes.add.submit': 'Añadir premio',
    'prizes.add.done': 'Premio añadido',

    'prizes.meta.title': 'Configuración de la app de Meta',
    'prizes.meta.hint': 'Meta solo envía los comentarios a una app que ha revisado, y solo las apps revisadas pueden mandar mensajes privados a gente de fuera del equipo. Hasta entonces, la app lee cada pocos minutos los comentarios de las publicaciones con un premio en marcha.',
    'prizes.meta.webhook': 'URL de callback del webhook',
    'prizes.meta.webhookHint': 'Meta → tu app → Webhooks. Suscribe los objetos Instagram y Page a los comentarios. El token de verificación es META_WEBHOOK_VERIFY_TOKEN en el servidor.',
    'prizes.meta.deletion': 'URL de callback de eliminación de datos',
    'prizes.meta.deletionHint': 'Meta → tu app → Configuración → Básica. Meta la llama cuando alguien quita la app de su cuenta.',
    'prizes.meta.instructions': 'URL de instrucciones de eliminación de datos',
    'prizes.meta.instructionsHint': 'La página pública que explica cómo pedir que se borren tus datos.',

    'prizes.erase.title': 'Borrar a una persona',
    'prizes.erase.hint': 'Si alguien pide que lo olvidemos, borra lo que los premios guardan de esa persona. También se borra sola al acabar el plazo de conservación. No se puede deshacer.',
    'prizes.erase.by': 'Buscar por',
    'prizes.erase.byName': 'El nombre que muestra',
    'prizes.erase.byId': 'Su ID en la red',
    'prizes.erase.name': 'Nombre',
    'prizes.erase.id': 'ID',
    'prizes.erase.submit': 'Borrar',
    'prizes.erase.ask': '¿Borrar todo lo que los premios guardan de «{who}»?',
    'prizes.erase.confirm': 'Se borra el registro de los premios que se le enviaron. No se puede deshacer.',
    'prizes.erase.deleted': { one: '{count} registro borrado', other: '{count} registros borrados' },
    'prizes.erase.nothing': 'No se guardaba nada de esa persona',

    'prizes.dialog.title': 'Premio · {network}',
    'prizes.dialog.running': 'En marcha',
    'prizes.dialog.off': 'Apagado',
    'prizes.dialog.counts': '{sent} enviados · {pending} en espera · {skipped} omitidos · {failed} fallidos',
    'prizes.dialog.publicLink': 'Enlace público',
    'prizes.dialog.copyLink': 'Copiar enlace',
    'prizes.dialog.openUntil': 'Abierto hasta el {date}. Cualquiera con el enlace puede abrirlo.',

    'prizes.rule.settingsLink': 'Ajustes → Premios',
    'prizes.rule.off': 'Los premios están desactivados en esta marca. Un administrador los activa en',
    'prizes.rule.needsReconnect': 'Esta cuenta se conectó sin permiso para enviar mensajes privados. Un administrador activa los premios en Ajustes → Premios y vuelve a conectar la cuenta; entonces el inicio de sesión lo pide.',
    'prizes.rule.howPrivate': 'Quien comente la palabra clave en esta publicación de {network} recibe el premio por mensaje privado, enviado por la app. Cada persona recibe cada premio una vez.',
    'prizes.rule.howPublic': 'Las publicaciones de {network} no pueden enviar mensajes privados desde aquí. El premio tiene una página pública: fija un comentario en la publicación que lleve al enlace de abajo.',
    'prizes.rule.noPrize': 'Todavía no hay ningún premio que dar. Añade uno, un archivo o un enlace, en',
    'prizes.rule.prize': 'Premio',
    'prizes.rule.keyword': 'Palabra clave',
    'prizes.rule.keywordHint': 'Ganan los comentarios que contienen esta palabra (sin importar mayúsculas ni tildes, como palabra entera).',
    'prizes.rule.keywordPlaceholder': 'receta',
    'prizes.rule.note': 'Nota para ti',
    'prizes.rule.noteHint': 'No la ve nadie. La gente ve el nombre del premio en su página.',
    'prizes.rule.notePlaceholder': 'sorteo en el comentario fijado',
    'prizes.rule.message': 'Mensaje',
    'prizes.rule.messageHint': 'Huecos: {{name}}, {{prize}}, {{link}} (obligatorio), {{hours}}. La nota de abajo se añade a cada mensaje.',
    'prizes.rule.defaultMessage': '¡Hola, {{name}}! Aquí tienes {{prize}}: {{link}}\n\nEl enlace vale {{hours}} horas.',
    'prizes.rule.previewAria': 'Cómo se ve el mensaje',
    'prizes.rule.previewTitle': 'Lo que recibe {name}',
    'prizes.rule.hours': 'El enlace vale (horas)',
    'prizes.rule.days': 'La página pública sigue abierta (días)',
    'prizes.rule.confirm': 'El texto de esta publicación dice que la respuesta es automática y qué se hace con los datos.',
    'prizes.rule.confirmHint': 'Obligatorio: sin esto el premio no se pone en marcha. Además, cada mensaje termina con «{notice}».',
    'prizes.rule.running': 'En marcha',
    'prizes.rule.runningHint': '(desmárcalo para dejar de darlo sin perder la configuración)',
    'prizes.rule.savedOn': 'Premio guardado: está en marcha',
    'prizes.rule.savedOff': 'Premio guardado, apagado',

    'prizes.preview.name': 'Alex',
    'prizes.preview.prize': 'el premio',

    'prizes.people.title': 'Personas',
    'prizes.people.none': 'Todavía no lo ha ganado nadie.',
    'prizes.people.person': 'Persona',
    'prizes.people.commented': 'Comentó',
    'prizes.people.result': 'Resultado',
    'prizes.people.downloads': { one: '{count} descarga', other: '{count} descargas' },
    'prizes.people.retention': 'Solo se guarda el nombre que muestran, y se borra solo al acabar el plazo fijado en Ajustes → Premios.',
    'prizes.delivery.sent': 'Enviado',
    'prizes.delivery.pending': 'En espera',
    'prizes.delivery.skipped': 'Omitido',
    'prizes.delivery.failed': 'Fallido',
    'prizes.reason.rule_off': 'El premio se apagó antes de poder enviarlo',
    'prizes.reason.too_old': 'El comentario tiene más de los 7 días que da la red para responder en privado',
    'prizes.reason.already_received': 'Esta persona ya recibió este premio',

    // the public prize page
    'prizes.public.tabTitle': '{name} · premio',
    'prizes.public.tabTitleEmpty': 'Tu premio',
    'prizes.public.from': 'De parte de {brand}',
    'prizes.public.download': 'Descargar',
    'prizes.public.open': 'Abrir',
    'prizes.public.opening': 'Un momento…',
    'prizes.public.until': 'Este enlace vale hasta el {date} y solo se puede usar unas pocas veces.',
    'prizes.public.errorTitle': 'Este enlace ya no funciona',
    'prizes.public.errorHint': 'Si te lo mandó una marca, pídele uno nuevo.',
    'prizes.public.error.expired': 'Ha caducado.',
    'prizes.public.error.used_up': 'Ya se ha usado todas las veces que permite.',
    'prizes.public.error.not_found': 'No existe o ya no está disponible.',
    'prizes.public.error.rate_limited': 'Demasiados intentos seguidos. Espera un momento y vuelve a probar.',
    'prizes.public.error.other': 'No se ha podido abrir.',
    'prizes.public.error.network': 'No se ha podido contactar con el servidor. Comprueba la conexión.',

    // the data-deletion page
    'prizes.deletion.title': 'Borrar tus datos',
    'prizes.deletion.handled': 'Tu solicitud ({code}) se atendió el {date}.',
    'prizes.deletion.deleted': { one: 'Borramos {count} registro sobre ti.', other: 'Borramos {count} registros sobre ti.' },
    'prizes.deletion.nothing': 'No guardábamos nada sobre ti.',
    'prizes.deletion.unknown': 'No encontramos ninguna solicitud con el código {code}.',
    'prizes.deletion.what': 'Si comentaste en una publicación para conseguir un premio, solo guardamos tu ID en la red y el nombre que muestras allí, para poder enviártelo. Se borra solo al cabo de unas semanas.',
    'prizes.deletion.sooner': 'Para que se borre antes:',
    'prizes.deletion.step1': 'Abre los ajustes de tu cuenta de Facebook o Instagram.',
    'prizes.deletion.step2': 'Ve a',
    'prizes.deletion.path': 'Configuración → Apps y sitios web (o Integraciones comerciales)',
    'prizes.deletion.step3': 'Quita la app. La red nos avisa y borramos al momento lo que guardábamos sobre ti.',
    'prizes.deletion.other': 'También puedes pedir a la cuenta en la que comentaste que te borre.',
  },
  en: {
    'prizes.kind.file': 'File',
    'prizes.kind.link': 'Link',

    'prizes.settings.title': 'Prizes for commenting',
    'prizes.settings.on': 'On',
    'prizes.settings.off': 'Off',
    'prizes.settings.enable': 'Use prizes in this brand',
    'prizes.settings.enableHint': 'Switching it on makes the next Meta sign-in ask for the permission to send messages. Accounts connected before have to be connected again (Accounts → Renew) to grant it; Meta may also need to review the app for it.',
    'prizes.settings.retention': 'Keep people for (days)',
    'prizes.settings.retentionHint': 'Who commented is deleted after this. At least 8: Meta allows 7 days to answer a comment.',
    'prizes.settings.notice': 'Note added to every message',
    'prizes.settings.noticeHint': 'Says the message is automatic. It cannot be left out.',

    'prizes.library.title': 'Prizes',
    'prizes.library.count': { one: '{count} prize', other: '{count} prizes' },
    'prizes.library.empty': 'No prizes yet',
    'prizes.library.emptyHint': 'Add a file to give out, or a link to somewhere else.',
    'prizes.library.name': 'Name',
    'prizes.library.what': 'What',
    'prizes.library.state': 'State',
    'prizes.library.running': { one: 'Running on {count} post', other: 'Running on {count} posts' },
    'prizes.library.archive': 'Archive',
    'prizes.library.archiveTitle': 'Archive “{name}”?',
    'prizes.library.archiveConfirm': 'Posts already running it keep working; it cannot be chosen for new ones.',
    'prizes.state.ready': 'Ready',
    'prizes.state.missing': 'File missing',
    'prizes.state.archived': 'Archived',

    'prizes.add.title': 'Add a prize',
    'prizes.add.kind': 'Kind of prize',
    'prizes.add.link': 'A link',
    'prizes.add.file': 'A file kept here',
    'prizes.add.name': 'Name',
    'prizes.add.nameHint': 'What people see on the page and in the message.',
    'prizes.add.url': 'Link',
    'prizes.add.urlHint': 'Where the person is sent. Anyone who gets the message can open it, so do not link to anything private.',
    'prizes.add.fileLabel': 'File',
    'prizes.add.fileHint': 'Up to 200 MB. People get a download link that works for a short time and a few downloads.',
    'prizes.add.fileAria': 'Prize file',
    'prizes.add.chooseFile': 'Choose the file to give',
    'prizes.add.hashing': 'Checking the file…',
    'prizes.add.uploading': 'Uploading… {pct}%',
    'prizes.add.finishing': 'Finishing…',
    'prizes.add.submit': 'Add prize',
    'prizes.add.done': 'Prize added',

    'prizes.meta.title': 'Meta app setup',
    'prizes.meta.hint': 'Meta only pushes comments to an app it has reviewed, and only reviewed apps may send private messages to people outside the team. Until then the app reads the comments of posts with a running prize every few minutes instead.',
    'prizes.meta.webhook': 'Webhook callback URL',
    'prizes.meta.webhookHint': 'Meta → your app → Webhooks. Subscribe the Instagram and Page objects to comments. The verify token is META_WEBHOOK_VERIFY_TOKEN on the server.',
    'prizes.meta.deletion': 'Data deletion callback URL',
    'prizes.meta.deletionHint': 'Meta → your app → Settings → Basic. Meta calls it when someone removes the app from their account.',
    'prizes.meta.instructions': 'Data deletion instructions URL',
    'prizes.meta.instructionsHint': 'The public page that tells people how to have their data deleted.',

    'prizes.erase.title': 'Erase a person',
    'prizes.erase.hint': 'If someone asks to be forgotten, delete what prizes kept about them. People are also deleted automatically when the retention period ends. This cannot be undone.',
    'prizes.erase.by': 'By',
    'prizes.erase.byName': 'The name they show',
    'prizes.erase.byId': 'Their id on the network',
    'prizes.erase.name': 'Name',
    'prizes.erase.id': 'Id',
    'prizes.erase.submit': 'Erase',
    'prizes.erase.ask': 'Delete everything prizes kept about “{who}”?',
    'prizes.erase.confirm': 'The record of the prizes sent to them is deleted. This cannot be undone.',
    'prizes.erase.deleted': { one: 'Deleted {count} entry', other: 'Deleted {count} entries' },
    'prizes.erase.nothing': 'Nothing was kept about that person',

    'prizes.dialog.title': 'Prize · {network}',
    'prizes.dialog.running': 'Running',
    'prizes.dialog.off': 'Switched off',
    'prizes.dialog.counts': '{sent} sent · {pending} waiting · {skipped} skipped · {failed} failed',
    'prizes.dialog.publicLink': 'Public link',
    'prizes.dialog.copyLink': 'Copy link',
    'prizes.dialog.openUntil': 'Open until {date}. Anyone with the link can open it.',

    'prizes.rule.settingsLink': 'Settings → Prizes',
    'prizes.rule.off': 'Prizes are switched off for this brand. An admin switches them on in',
    'prizes.rule.needsReconnect': 'This account was connected without the permission to send private messages. An admin switches prizes on in Settings → Prizes and connects the account again; the sign-in then asks for it.',
    'prizes.rule.howPrivate': 'Anyone who comments the keyword on this {network} post gets the prize as a private message, by the app itself. Each person gets each prize once.',
    'prizes.rule.howPublic': '{network} posts cannot send private messages here. The prize gets a public page instead: pin a comment on the post that points people to the link below.',
    'prizes.rule.noPrize': 'There is no prize to give yet. Add one, a file or a link, in',
    'prizes.rule.prize': 'Prize',
    'prizes.rule.keyword': 'Keyword',
    'prizes.rule.keywordHint': 'Comments that contain this word (any case, accents ignored, as a whole word) win the prize.',
    'prizes.rule.keywordPlaceholder': 'recipe',
    'prizes.rule.note': 'Note to yourself',
    'prizes.rule.noteHint': 'Not shown to anyone. People see the name of the prize on its page.',
    'prizes.rule.notePlaceholder': 'giveaway in the pinned comment',
    'prizes.rule.message': 'Message',
    'prizes.rule.messageHint': 'Placeholders: {{name}}, {{prize}}, {{link}} (required), {{hours}}. The note below is added to every message.',
    'prizes.rule.defaultMessage': 'Hi {{name}}! Here is {{prize}}: {{link}}\n\nThe link works for {{hours}} hours.',
    'prizes.rule.previewAria': 'How the message looks',
    'prizes.rule.previewTitle': 'What {name} receives',
    'prizes.rule.hours': 'The link works for (hours)',
    'prizes.rule.days': 'The public page stays open for (days)',
    'prizes.rule.confirm': 'The text of this post tells people that the reply is automatic and what is done with their data.',
    'prizes.rule.confirmHint': 'Required: a prize does not run without it. Every message also ends with “{notice}”.',
    'prizes.rule.running': 'Running',
    'prizes.rule.runningHint': '(untick to stop giving it out without losing the settings)',
    'prizes.rule.savedOn': 'Prize saved: it is running',
    'prizes.rule.savedOff': 'Prize saved, switched off',

    'prizes.preview.name': 'Alex',
    'prizes.preview.prize': 'the prize',

    'prizes.people.title': 'People',
    'prizes.people.none': 'Nobody has won it yet.',
    'prizes.people.person': 'Person',
    'prizes.people.commented': 'Commented',
    'prizes.people.result': 'Result',
    'prizes.people.downloads': { one: '{count} download', other: '{count} downloads' },
    'prizes.people.retention': 'Only the name they show is kept, and it is deleted automatically when the retention period set in Settings → Prizes ends.',
    'prizes.delivery.sent': 'Sent',
    'prizes.delivery.pending': 'Waiting',
    'prizes.delivery.skipped': 'Skipped',
    'prizes.delivery.failed': 'Failed',
    'prizes.reason.rule_off': 'The prize was switched off before it could be sent',
    'prizes.reason.too_old': 'The comment is older than the 7 days the network allows for a private reply',
    'prizes.reason.already_received': 'This person already received this prize',

    // the public prize page
    'prizes.public.tabTitle': '{name} · prize',
    'prizes.public.tabTitleEmpty': 'Your prize',
    'prizes.public.from': 'From {brand}',
    'prizes.public.download': 'Download',
    'prizes.public.open': 'Open',
    'prizes.public.opening': 'One moment…',
    'prizes.public.until': 'This link works until {date}. It cannot be used more than a few times.',
    'prizes.public.errorTitle': 'This link no longer works',
    'prizes.public.errorHint': 'If you were sent this link by a brand, ask them for a new one.',
    'prizes.public.error.expired': 'It has expired.',
    'prizes.public.error.used_up': 'It has been used the most times it allows.',
    'prizes.public.error.not_found': 'It does not exist or is no longer available.',
    'prizes.public.error.rate_limited': 'Too many tries in a row. Wait a moment and try again.',
    'prizes.public.error.other': 'It could not be opened.',
    'prizes.public.error.network': 'Could not reach the server. Check your connection.',

    // the data-deletion page
    'prizes.deletion.title': 'Deleting your data',
    'prizes.deletion.handled': 'Your request ({code}) was handled on {date}.',
    'prizes.deletion.deleted': { one: 'We deleted {count} entry about you.', other: 'We deleted {count} entries about you.' },
    'prizes.deletion.nothing': 'We were not keeping anything about you.',
    'prizes.deletion.unknown': 'We could not find a request with the code {code}.',
    'prizes.deletion.what': 'If you commented on a post to get a prize, we kept only your id on the network and the name you show there, so we could send it to you. That is deleted automatically after a few weeks.',
    'prizes.deletion.sooner': 'To have it deleted sooner:',
    'prizes.deletion.step1': 'Open the settings of your Facebook or Instagram account.',
    'prizes.deletion.step2': 'Go to',
    'prizes.deletion.path': 'Settings → Apps and websites (or Business integrations)',
    'prizes.deletion.step3': 'Remove the app. The network tells us, and we delete what we kept about you straight away.',
    'prizes.deletion.other': 'You can also ask the account you commented on to erase you.',
  },
});
